import { memo, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Box, Chip, Icon, Skeleton, Typography, useMediaQuery } from "@mui/material";
import { keyframes, useTheme } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "src/utils/AuthContext.js";
import { getPageRoute } from "src/services";
import { trackTileClicked } from "src/utils/sectionAnalytics.js";
import { getEventUniqueNumber } from "src/utils/Utils.js";
import { useDisplayImage } from "src/utils/useDisplayImage.js";
import { bakedTileCandidates, calendarProfileImageUrl, registerCachedImage, resizedImageCandidates } from "src/utils/imageUrl.js";
import { useInViewOnce } from "src/utils/useInViewOnce.js";
import { useUpcomingEventStatus } from "src/utils/useUpcomingEventStatus.js";
import DefaultImageBasketball from "src/components/blocks/event/DefaultImageBasketball.js";
import EventProfileBg from "src/components/blocks/event/EventProfileBg.js";
import SubscribeButton from "src/components/blocks/SubscribeButton.js";
import SubscriberStatusBadge from "src/components/blocks/SubscriberStatusBadge.js";
import FreeBadge from "./FreeBadge.js";
import {
  eventArtworkSources,
  eventJcalDates,
  isTvCalendar,
  pastEventLabel,
  eventMatchupLabel,
  tileMetaLabel,
  toSubscribeCalendar,
} from "./calendarTileUtils.js";
import { networkBadgeFromDescription } from "./logoRails.js";

/** Thumbnail height. `EventProfileBg` needs a px value to size its skeleton. */
export const THUMB_HEIGHT = { compact: 150, wide: 185 };

/** Portrait poster height (~2:3) for TV/movie rails. */
export const POSTER_THUMB_HEIGHT = { compact: 225, wide: 285 };

/** Vertical lead so a rail below the fold resolves its art before it scrolls in. */
const ART_ROOT_MARGIN = "400px";

/** How long a fallback candidate may sit with neither onLoad nor onError
 *  before it is treated as failed (hung service worker / stalled CDN). */
const FALLBACK_LOAD_TIMEOUT_MS = 3000;

/** The live-dot pulse on the ticker's "Live" state. */
const pulse = keyframes({ "0%,100%": { opacity: 1 }, "50%": { opacity: 0.35 } });

/** Ticker chip palette per state: live (red), imminent countdown (amber), else dark. */
const TICKER_VARIANT_SX = {
  live: { backgroundColor: "error.main", color: "#fff" },
  soon: { backgroundColor: "warning.main", color: "#111" },
  normal: { backgroundColor: "overlay.black70", color: "#fff" },
};

/**
 * The thumbnail. Split out so the image pipeline — a fetch, a canvas pass and a
 * palette extraction per tile — mounts only once the tile can be seen.
 *
 * @param {{ tileCalendar: object, thumbHeight: number, fill?: boolean,
 *   homeTeamLogo?: string|null, eventImageUrl?: string|null,
 *   background?: string|null }} props
 *   `fill` makes the image cover the thumbnail (full-bleed posters).
 */
/** Derivative chains that already exhausted once this session — later mounts
 *  skip them instead of refiring known-missing requests. */
const exhaustedDerivativeChains = new Set();
const MAX_EXHAUSTED_ENTRIES = 1000;

/** Bounded insert — evicts the oldest entry past the cap. */
const rememberExhaustedChain = (key) => {
  exhaustedDerivativeChains.add(key);
  if (exhaustedDerivativeChains.size > MAX_EXHAUSTED_ENTRIES) {
    exhaustedDerivativeChains.delete(exhaustedDerivativeChains.values().next().value);
  }
};

/**
 * Plain img walking a candidate list: onError advances, a watchdog advances a
 * response that neither loads nor errors, exhaustion calls `onExhausted`.
 * The img paints only once loaded — a skeleton covers the frame meanwhile, so
 * a failed candidate never shows the browser's broken-image glyph.
 *
 * @param {{ candidates: string[], height: number, alt: string,
 *   onExhausted?: () => void }} props
 */
const FallbackImage = memo(({ candidates, height, alt, onExhausted }) => {
  const [index, setIndex] = useState(0);
  const [loadedSrc, setLoadedSrc] = useState(null);
  const src = candidates[index];

  // New candidates (late hydration) restart the walk from the top.
  useEffect(() => {
    setIndex(0);
    setLoadedSrc(null);
  }, [candidates]);

  // Closure-based on purpose: a watchdog and a late onError firing for the
  // same candidate resolve to the same index, so they cannot double-skip.
  const advance = useCallback(() => {
    if (index + 1 < candidates.length) setIndex(index + 1);
    else onExhausted?.();
  }, [index, candidates, onExhausted]);

  useEffect(() => {
    if (!src || loadedSrc === src) return undefined;
    const timer = setTimeout(advance, FALLBACK_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [src, loadedSrc, advance]);

  if (!src) return null;

  const loaded = loadedSrc === src;

  return (
    <Box
      sx={{
        height: `${height}px`,
        width: "100%",
        backgroundColor: "#f0f0f0",
        borderRadius: "8px",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {!loaded && (
        <Skeleton
          data-testid="fallback-image-skeleton"
          animation="wave"
          variant="rectangular"
          width="100%"
          height="100%"
          sx={{ position: "absolute", inset: 0, borderRadius: "8px" }}
        />
      )}
      {/* No native lazy: the rail's own art window mounts slides ahead of the
          drag, and horizontally-offscreen slides never intersect the viewport,
          so `loading="lazy"` would hold their fetch until dragged into view. */}
      <img
        src={src}
        alt={alt}
        decoding="async"
        // Registers the cached settle state React missed: a loaded one disarms
        // the watchdog above, a failed one advances instead of sitting broken.
        ref={(img) =>
          registerCachedImage(img, { onLoaded: () => setLoadedSrc(src), onFailed: advance })
        }
        onLoad={() => setLoadedSrc(src)}
        onError={advance}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
          visibility: loaded ? "visible" : "hidden",
        }}
      />
    </Box>
  );
});
FallbackImage.displayName = "FallbackImage";

/**
 * Flat poster art. A poster covers its whole frame, so the gradient, palette
 * query and candidate machinery EventProfileBg runs would paint invisible
 * pixels — a plain img with a short fallback chain replaces all of it.
 */
const PosterArt = memo(({ tileCalendar, thumbHeight, background }) => {
  const candidates = useMemo(
    () =>
      [
        ...resizedImageCandidates(background),
        background,
        calendarProfileImageUrl(tileCalendar),
      ].filter(Boolean),
    [background, tileCalendar],
  );
  const [exhausted, setExhausted] = useState(false);

  // New art after exhaustion (late hydration) retries the chain.
  useEffect(() => setExhausted(false), [candidates]);

  if (!candidates.length || exhausted) {
    return (
      <DefaultImageBasketball
        loading={false}
        height={`${thumbHeight}px`}
        width="100%"
        eventUniqueNumber={getEventUniqueNumber(String(tileCalendar._id), 0)}
      />
    );
  }

  return (
    <FallbackImage
      candidates={candidates}
      height={thumbHeight}
      alt={tileCalendar.name}
      onExhausted={() => setExhausted(true)}
    />
  );
});
PosterArt.displayName = "PosterArt";

const TileArt = memo(({
  tileCalendar,
  thumbHeight,
  fill = false,
  homeTeamLogo,
  eventImageUrl,
  background,
}) => {
  // Server-baked composite first; the client pipeline below stays the
  // fallback, so a missing derivative degrades to today's behavior. A chain
  // that already exhausted this session is skipped outright.
  const baked = useMemo(() => {
    const chain = bakedTileCandidates(calendarProfileImageUrl(tileCalendar));
    return chain.length && exhaustedDerivativeChains.has(chain[0]) ? [] : chain;
  }, [tileCalendar]);
  const [bakedExhausted, setBakedExhausted] = useState(false);

  useEffect(() => setBakedExhausted(false), [baked]);

  if (baked.length > 0 && !bakedExhausted) {
    return (
      <FallbackImage
        candidates={baked}
        height={thumbHeight}
        alt={tileCalendar.name}
        onExhausted={() => {
          rememberExhaustedChain(baked[0]);
          setBakedExhausted(true);
        }}
      />
    );
  }

  return (
    <LegacyTileArt
      tileCalendar={tileCalendar}
      thumbHeight={thumbHeight}
      fill={fill}
      homeTeamLogo={homeTeamLogo}
      eventImageUrl={eventImageUrl}
      background={background}
    />
  );
});
TileArt.displayName = "TileArt";

const LegacyTileArt = memo(({
  tileCalendar,
  thumbHeight,
  fill = false,
  homeTeamLogo,
  eventImageUrl,
  background,
}) => {
  const { displayImage, loading, handleImageError } = useDisplayImage({
    type: "RecommendedCalendar",
    calendar: tileCalendar,
    homeTeamLogo,
    eventImageUrl,
    background,
  });

  return displayImage ? (
    <EventProfileBg
      loading={loading}
      profileImage={displayImage}
      calendarId={tileCalendar._id}
      eventSummary={tileCalendar.name}
      height={`${thumbHeight}px`}
      width="100%"
      fill={fill}
      handleImageError={handleImageError}
    />
  ) : (
    <DefaultImageBasketball
      loading={loading}
      height={`${thumbHeight}px`}
      width="100%"
      eventUniqueNumber={getEventUniqueNumber(String(tileCalendar._id), 0)}
    />
  );
});
LegacyTileArt.displayName = "LegacyTileArt";

/** The white circle shared by the bottom-left poster badges (network + media kind). */
const CORNER_BADGE_SX = {
  position: "absolute",
  bottom: 8,
  left: 8,
  width: "34px",
  height: "34px",
  borderRadius: "50%",
  backgroundColor: "#fff",
  boxShadow: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
};

/**
 * Bottom-left media-kind badge on a poster with no network logo: a film glyph
 * for movies, a TV glyph for shows, so the format reads at a glance.
 *
 * @param {{ kind: "movie"|"tv" }} props
 */
const MediaKindCircle = ({ kind }) => (
  <Box data-testid={`tile-media-badge-${kind}`} sx={{ ...CORNER_BADGE_SX, backgroundColor: "#111" }}>
    <Icon baseClassName="material-symbols-outlined" sx={{ fontSize: "1.15rem", color: "#fff" }}>
      {kind === "movie" ? "local_movies" : "tv"}
    </Icon>
  </Box>
);

/**
 * Bottom-left network badge on a poster: the brand-mark icon or wordmark in a
 * white circle, falling back to the network's initials if the image 404s.
 *
 * @param {{ badge: { name: string, logoUrl: string, isIcon: boolean } }} props
 */
const NetworkCircle = ({ badge }) => {
  const [failed, setFailed] = useState(false);
  const showImage = badge.logoUrl && !failed;
  return (
    <Box sx={CORNER_BADGE_SX}>
      {showImage ? (
        <Box
          component="img"
          src={badge.logoUrl}
          alt={badge.name}
          ref={(img) => registerCachedImage(img, { onFailed: () => setFailed(true) })}
          onError={() => setFailed(true)}
          sx={{
            width: badge.isIcon ? "60%" : "86%",
            height: badge.isIcon ? "60%" : "64%",
            objectFit: "contain",
          }}
        />
      ) : (
        <Typography sx={{ fontSize: "11px", fontWeight: 800, color: "#111", lineHeight: 1 }}>
          {badge.name.slice(0, 3).toUpperCase()}
        </Typography>
      )}
    </Box>
  );
};

/**
 * One calendar in a rail: thumbnail with the prod live/countdown ticker top-left,
 * a Free chip top-right, a network badge bottom-left on TV posters, the name
 * below, and a subscribe button.
 *
 * Image resolution matches the recommended-calendar card: `useDisplayImage`
 * resolves the creator picture, `EventProfileBg` paints it over a palette
 * gradient, and `DefaultImageBasketball` covers calendars with no image.
 *
 * Fills its container, so the rail slide or grid cell sets the width.
 *
 * @param {{ calendar: object, section: object, onSubscribeAction?: Function,
 *   preloadArt?: boolean, poster?: boolean }} props - `preloadArt` skips the
 *   visibility gate for the slides a rail opens on; `poster` renders a taller,
 *   full-bleed portrait thumbnail for TV/movie rails.
 */
const CalendarTile = ({ calendar, section, onSubscribeAction, preloadArt = false, poster = false }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [artRef, artInView] = useInViewOnce({ rootMargin: ART_ROOT_MARGIN });

  // `TileArt` and `SubscribeButton` both read the Calendar-document shape; the
  // sections payload carries the same fields under `calendarId`.
  const tileCalendar = useMemo(
    () => toSubscribeCalendar(calendar),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [calendar.calendarId, calendar.handle, calendar.name, calendar.picture, calendar.stanzaLink],
  );
  const artwork = useMemo(
    () => eventArtworkSources(calendar.mostRecentEvent, calendar.handle),
    [calendar.mostRecentEvent, calendar.handle],
  );

  const heights = poster ? POSTER_THUMB_HEIGHT : THUMB_HEIGHT;
  const thumbHeight = isMediumScreen ? heights.compact : heights.wide;
  const { dtstart, dtend } = useMemo(
    () => eventJcalDates(calendar.mostRecentEvent),
    [calendar.mostRecentEvent],
  );
  const { isLive, isUpcoming, upcomingText, isCountdown } = useUpcomingEventStatus(dtstart, dtend);
  const lastEventLabel = useMemo(
    () => pastEventLabel(calendar.mostRecentEvent),
    [calendar.mostRecentEvent],
  );
  // Bottom-left media-kind badge: movies (their section category) get a film
  // glyph; TV posters get the network logo, or a generic TV glyph when unknown.
  const isMovieTile = poster && section?.category === "movies";
  const isTvTile =
    poster && !isMovieTile && (section?.category === "tv" || isTvCalendar(calendar));
  const networkBadge = useMemo(
    () => (isTvTile ? networkBadgeFromDescription(calendar.description) : null),
    [isTvTile, calendar.description],
  );
  // Shared below-name line: next event when upcoming, else subscriber status.
  // Timely sections ("MLB Games This Week") lead with the matchup itself.
  const metaLabel = useMemo(
    () =>
      (section?.category === "timely" && eventMatchupLabel(calendar.mostRecentEvent, calendar.name)) ||
      tileMetaLabel(calendar),
    [calendar, section?.category],
  );

  // Top-left ticker: Live now, else the upcoming countdown / "N days out", else
  // a "Last event · N ago" for a recently-past event (nothing for stale ones).
  let ticker = null;
  if (isLive) {
    ticker = { label: "Live", variant: "live" };
  } else if (isUpcoming && upcomingText) {
    ticker = { label: upcomingText, variant: isCountdown ? "soon" : "normal" };
  } else if (lastEventLabel) {
    ticker = { label: lastEventLabel, variant: "normal" };
  }

  const handleOpen = () => {
    trackTileClicked({ user, section, calendar });
    navigate(
      getPageRoute("calendar", "CALENDAR_EVENTS", {
        handle: calendar.handle,
        calendarId: calendar.calendarId,
      }),
    );
  };

  return (
    <Box
      role="link"
      tabIndex={0}
      data-testid="calendar-tile"
      onClick={handleOpen}
      onKeyDown={(event) => (event.key === "Enter" || event.key === " ") && handleOpen()}
      sx={{
        width: "100%",
        cursor: "pointer",
        "&:focus-visible": { outline: (theme) => `2px solid ${theme.palette.primary.main}` },
      }}
    >
      <Box
        ref={artRef}
        sx={{ position: "relative", width: "100%", borderRadius: "12px", overflow: "hidden" }}
      >
        {preloadArt || artInView ? (
          poster ? (
            <PosterArt
              tileCalendar={tileCalendar}
              thumbHeight={thumbHeight}
              background={artwork.background}
            />
          ) : (
            <TileArt tileCalendar={tileCalendar} thumbHeight={thumbHeight} {...artwork} />
          )
        ) : (
          // Matches the skeleton `EventProfileBg` paints, so a gated tile and a
          // resolving one are the same frame.
          <Skeleton
            animation="wave"
            variant="rectangular"
            sx={{ borderRadius: "4px" }}
            width="100%"
            height={thumbHeight}
          />
        )}

        {ticker && (
          <Chip
            size="small"
            data-testid="tile-ticker"
            label={
              ticker.variant === "live" ? (
                <Box component="span" sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Box
                    component="span"
                    sx={{
                      width: "6px",
                      height: "6px",
                      borderRadius: "50%",
                      backgroundColor: "#fff",
                      animation: `${pulse} 1.4s ease-in-out infinite`,
                    }}
                  />
                  Live
                </Box>
              ) : (
                ticker.label
              )
            }
            sx={{
              position: "absolute",
              top: 6,
              left: 6,
              height: "20px",
              fontSize: "0.65rem",
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
              ...TICKER_VARIANT_SX[ticker.variant],
            }}
          />
        )}

        {!calendar.isStanzaPlus && <FreeBadge testId="tile-free-badge" />}

        {networkBadge ? (
          <NetworkCircle badge={networkBadge} />
        ) : isMovieTile ? (
          <MediaKindCircle kind="movie" />
        ) : isTvTile ? (
          <MediaKindCircle kind="tv" />
        ) : null}
      </Box>

      {/* Name + subscriber count below the thumbnail. With no meta line the
          name takes its row as a second line instead; both shapes add up to
          the same height so subscribe buttons align across a rail. */}
      <Box sx={{ mt: 1 }}>
        <Typography
          variant="tileName"
          component="p"
          data-testid="calendar-name"
          title={calendar.name}
          sx={{
            color: "text.primary",
            ...(metaLabel
              ? { ...theme.ellipsis(), whiteSpace: "nowrap" }
              : {
                  height: "2.5em",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }),
          }}
        >
          {calendar.name}
        </Typography>
        <Box
          data-testid="tile-subscribers"
          sx={{
            minHeight: metaLabel ? "32px" : "12px",
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          <SubscriberStatusBadge sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {metaLabel}
          </SubscriberStatusBadge>
        </Box>
      </Box>

      {/* The button owns its own dialogs, so keep its clicks off the tile. */}
      <Box
        sx={{ mt: 1 }}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <SubscribeButton
          user={user}
          calendar={tileCalendar}
          handle={calendar.handle}
          onSubscribeAction={onSubscribeAction}
          isEventCard={true}
        />
      </Box>
    </Box>
  );
};

export default memo(CalendarTile);
